import { ArrowTopRightOnSquareIcon, MagnifyingGlassIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Group,
    Loader,
    Stack,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ROUTES } from '../../../constants/routes';
import { CandidateStatus } from '../../../models/opportunity.model';
import { FilteringOperator, SortDirection } from '../../../models/person.model';
import { PersonService } from '../../../services/personService';
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';
import { useRemoveShortlistEntry, useUpsertShortlistEntry } from '../hooks/useShortlistMutations';
import { useOpportunityShortlistQuery } from '../hooks/useShortlistQuery';

const personService = new PersonService();

const getAvatarSrc = (image: string | undefined): string | undefined => {
    if (!image) return undefined;
    if (image.startsWith('data:') || image.startsWith('http')) return image;
    if (/^[A-Za-z0-9+/=]+$/.test(image)) return `data:image/jpeg;base64,${image}`;
    return undefined;
};

const formatAvailability = (availabilityDate: string | undefined): { label: string; color: string } => {
    if (isPersonCurrentlyUnassigned(availabilityDate)) {
        return { label: 'Available now', color: 'sopraGreen' };
    }
    if (!availabilityDate) {
        return { label: 'Assigned', color: 'materialGray' };
    }
    const date = new Date(availabilityDate);
    const today = new Date();
    const isPast = date <= today;
    const label = isPast ? 'Available now' : `Avail. ${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    return { label, color: isPast ? 'sopraGreen' : 'sopraOrange' };
};

interface CandidateRowProps {
    id: number;
    firstName: string;
    lastName: string;
    image?: string;
    availabilityDate?: string;
    cvUpdateDate?: string;
    officeLocationName?: string;
    onShortlist: (id: number) => void;
    onOpenPreview: (id: number) => void;
    shortlisted: boolean;
    shortlistPending: boolean;
}

const CandidateRow = ({
    id,
    firstName,
    lastName,
    image,
    availabilityDate,
    cvUpdateDate,
    officeLocationName,
    onShortlist,
    onOpenPreview,
    shortlisted,
    shortlistPending,
}: CandidateRowProps) => {
    const availability = formatAvailability(availabilityDate);
    const cvUpdated = cvUpdateDate
        ? new Date(cvUpdateDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

    return (
        <Group justify="space-between" wrap="nowrap" py={6} px={4} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <UnstyledButton
                onClick={() => onOpenPreview(id)}
                style={{ flex: 1, minWidth: 0 }}
            >
                <Group gap="sm" wrap="nowrap">
                    <Avatar
                        src={getAvatarSrc(image)}
                        size="md"
                        radius="xl"
                        alt={`${firstName} ${lastName}`}
                    >
                        {firstName[0]}{lastName[0]}
                    </Avatar>
                    <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} style={{ color: 'var(--mantine-color-anchor)' }}>
                            {firstName} {lastName}
                        </Text>
                        {officeLocationName && (
                            <Text size="xs" c="dimmed" truncate>{officeLocationName}</Text>
                        )}
                    </Box>
                </Group>
            </UnstyledButton>

            <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                <Badge size="sm" variant="light" color={availability.color}>
                    {availability.label}
                </Badge>
                <Tooltip label={`CV updated: ${cvUpdated}`} withArrow>
                    <Badge size="sm" variant="outline" color="materialGray">
                        CV {cvUpdated}
                    </Badge>
                </Tooltip>
                <Stack gap={4}>
                    <Button
                        variant={shortlisted ? 'filled' : 'light'}
                        color="brand.5"
                        size="xs"
                        leftSection={shortlisted
                            ? <StarIconSolid style={{ width: 13, height: 13 }} />
                            : <StarIcon style={{ width: 13, height: 13 }} />
                        }
                        onClick={() => onShortlist(id)}
                        loading={shortlistPending}
                        disabled={shortlistPending}
                        aria-label={shortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                    >
                        {shortlisted ? 'Shortlisted' : 'Shortlist'}
                    </Button>
                    <Button
                        component="a"
                        href={ROUTES.CV_DETAILS(id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="brand.5"
                        size="xs"
                        leftSection={<ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />}
                        aria-label="Open full profile"
                    >
                        Full profile
                    </Button>
                </Stack>
            </Group>
        </Group>
    );
};

interface Props {
    opportunityId: number;
    positionId: number;
    onOpenPreview: (personId: number) => void;
}

export const PositionCandidateSearch = ({ opportunityId, positionId, onOpenPreview }: Props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchTerm, 350);
    const [pendingPersonId, setPendingPersonId] = useState<number | null>(null);

    const keywords = debouncedSearch.trim().length >= 2
        ? debouncedSearch.trim().split(/\s+/)
        : [];

    const { data, isLoading } = useQuery({
        queryKey: ['opportunity-candidate-search', positionId, keywords],
        queryFn: () =>
            personService.getPersonsByPageAndFilter(
                20,
                0,
                '',
                SortDirection.Invalid,
                keywords,
                false,
                null,
                undefined,
                0.85, 0.85, 5, 5,
                false, false, false,
                false,
                false,
                FilteringOperator.Or,
            ),
        enabled: keywords.length > 0,
        staleTime: 60 * 1000,
    });

    const { data: shortlistData } = useOpportunityShortlistQuery(opportunityId, positionId);
    const shortlistedIds = new Set((shortlistData ?? []).map((e) => e.personId));

    const upsertMutation = useUpsertShortlistEntry(opportunityId, positionId);
    const removeMutation = useRemoveShortlistEntry(opportunityId);

    const handleShortlist = (personId: number) => {
        setPendingPersonId(personId);
        if (shortlistedIds.has(personId)) {
            removeMutation.mutate(
                { positionId, personId },
                { onSettled: () => setPendingPersonId(null) },
            );
        } else {
            upsertMutation.mutate(
                { personId, status: CandidateStatus.Proposed, notes: null },
                { onSettled: () => setPendingPersonId(null) },
            );
        }
    };

    return (
        <Stack gap="xs" mt="sm">
            <TextInput
                placeholder="Search by name…"
                leftSection={<MagnifyingGlassIcon style={{ width: 16, height: 16 }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                size="sm"
                autoFocus
            />

            {isLoading && (
                <Group justify="center" py="sm">
                    <Loader size="sm" />
                </Group>
            )}

            {!isLoading && keywords.length > 0 && data?.page?.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="sm">No people found</Text>
            )}

            {!isLoading && keywords.length === 0 && searchTerm.trim().length > 0 && (
                <Text size="xs" c="dimmed" ta="center">Type at least 2 characters</Text>
            )}

            {keywords.length === 0 && searchTerm.trim().length === 0 && (
                <Text size="xs" c="dimmed" ta="center" py="xs">Start typing a name to search</Text>
            )}

            {data?.page && data.page.length > 0 && (
                <Box>
                    {data.page.map((person) => (
                        <CandidateRow
                            key={person.id}
                            id={person.id}
                            firstName={person.firstName}
                            lastName={person.lastName}
                            image={typeof person.image === 'string' ? person.image : undefined}
                            availabilityDate={person.availabilityDate}
                            cvUpdateDate={person.cvUpdateDate ? String(person.cvUpdateDate) : undefined}
                            officeLocationName={person.officeLocationName}
                            onShortlist={handleShortlist}
                            onOpenPreview={onOpenPreview}
                            shortlisted={shortlistedIds.has(person.id)}
                            shortlistPending={pendingPersonId === person.id}
                        />
                    ))}
                    {data.allDataCount > data.page.length && (
                        <Text size="xs" c="dimmed" ta="center" pt="xs">
                            Showing {data.page.length} of {data.allDataCount} — refine your search
                        </Text>
                    )}
                </Box>
            )}
        </Stack>
    );
};
